import { DatePipe } from '@angular/common';
import { afterNextRender, booleanAttribute, ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, DOCUMENT, effect, ElementRef, inject, input, numberAttribute, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { interval, timer } from 'rxjs';
import { ReserveResponse } from 'src/app/components/consulting-reservation';
import { ConsultingReservationService } from 'src/app/components/consulting-reservation/consulting-reservation.service';
import { LabelComponent } from 'src/app/components/consulting-reservation/step2-simple/label/label.component';
import { createForm } from 'src/app/components/consulting-reservation/step2-simple/step2.form';

@Component({
    selector: 'app-step2-simple',
    imports: [
      DatePipe,
      ReactiveFormsModule,
      MatFormFieldModule,
      MatInputModule,
      MatProgressBarModule,
      LabelComponent
    ],
    templateUrl: './step2.component.html',
    styleUrls: ['./step2.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class Step2SimpleComponent {
  readonly #reservationService = inject(ConsultingReservationService);
  readonly #cd = inject(ChangeDetectorRef);

  readonly hideDatePart = input(false, { transform: booleanAttribute });
  readonly hasNextStep = input(false, { transform: booleanAttribute });
  readonly discount = input.required({ transform: numberAttribute });
  readonly isCasual = input.required({ transform: booleanAttribute });
  readonly isRequestMaterial = input(false, { transform: booleanAttribute });
  readonly completeUrl = input.required<string>();

  readonly countries = input('');
  readonly cities = input('');

  readonly complete = output<boolean>();

  readonly selected = signal<any>({});
  readonly sending = signal<boolean>(false);
  readonly expirationCountdown = signal<string>('10:00');
  protected form = createForm();

  readonly inTarget = signal(false);
  readonly remain = signal<number>(Object.keys(this.form.controls).length - 1);
  readonly percentage = signal<number>(0);
  readonly next = signal<string>('');
  readonly counselingText = computed(() => {
    return this.isCasual() ? '電話' : '無料';
  });

  protected readonly countriesArray = computed(() => {
    const cities = this.cities().split(',').map(city => city.trim()).filter(city => city !== '');
    const countries = this.countries().split(',').map(country => country.trim()).filter(country => country !== '');
    if (cities.length === 0) {
      if (countries.length === 0) {
        countries.push(...['オーストラリア', 'イギリス', 'カナダ', 'アメリカ', 'アイルランド', 'ニュージーランド', 'マルタ', 'フィリピン']);
      }
      cities.push(...countries);
    }
    cities.push('その他', 'まだわからない');
    return cities;
  });
  protected readonly hasCity = computed(() => {
    const cities = this.cities().split(',').map(city => city.trim()).filter(city => city !== '');
    return cities.length > 0;
  });

  readonly #baseTime = new Date();

  readonly showPercentage = computed(() => {
    this.inTarget();
    this.remain();
    return this.inTarget() && this.remain() > 0;
  });

  constructor() {
    const value = sessionStorage.getItem('counseling');
    if (!value) {
      this.#reservationService.replace();
    }
    this.form.patchValue(JSON.parse(value ?? '{}'));
    this.selected.set(JSON.parse(value ?? '{}'));

    const document = inject(DOCUMENT);
    const element = inject(ElementRef).nativeElement;

    effect(() => {
      const value = this.isRequestMaterial();
      this.form = createForm(value);
    });

    afterNextRender(() => {
      if (!this.hideDatePart()) {
        timer(30)
          .subscribe(() => {
            const element = document.querySelector('form') as HTMLElement;
            element.scrollIntoView({ behavior: 'instant', block: 'start' });
          });
      }

      const options = { root: document };
      const callback = (entries: { isIntersecting: boolean }[]) => {
        entries.forEach(entry => {
          this.inTarget.set(entry.isIntersecting);
        });
      }

      const observerObject = new IntersectionObserver(callback, options);
      observerObject.observe(element);
    });


    interval(1000)
      .pipe(
        takeUntilDestroyed()
      ).subscribe(x => {
        this.#countDown(x);
      });

    this.form.valueChanges
      .pipe(
        takeUntilDestroyed()
      )
      .subscribe(values => {
        const value = JSON.parse(sessionStorage.getItem('counseling') ?? '{}')!;
        Object.assign(value, values);
        sessionStorage.setItem('counseling', JSON.stringify(value));
        this.countValue();
        this.#cd.markForCheck();
      });

    this.countValue();
  }

  countValue() {
    const controls = Object.values(this.form.controls);
    const total = controls.length - 1;
    const ok = controls.filter(x => x.valid).length - 1;

    const next = controls.findIndex(x => x.invalid);


    this.remain.set(total - ok);
    this.percentage.set(Math.floor((ok / total) * 100));
    this.next.set(next >= 0 ? Object.keys(this.form.controls)[next] : '');
  }

  #countDown(count: number) {
    const now = new Date();
    const diff = Math.min(Math.floor((now.getTime() - this.#baseTime.getTime()) / 1000), 60*10);

    const dt = new Date(0, 0, 0, 0, 10, 0);
    dt.setTime(dt.getTime() - diff * 1000);

    const minutes = dt.getMinutes().toString().padStart(2, '0');
    const seconds = dt.getSeconds().toString().padStart(2, '0');
    this.expirationCountdown.set(`${minutes}:${seconds}`);
  }

  requireLabel(control: FormControl) {
    return control.valid ? '' : '必須';
  }

  countryChange(event: any) {
    const country = event.target.value;
    const currentValue = this.form.getRawValue().countries;
    if (event.target.checked) {
      if (!currentValue.includes(country)) {
        currentValue.push(country);
      }
    } else {
      if (currentValue.includes(country)) {
        const index = currentValue.indexOf(country);
        currentValue.splice(index, 1);
      }
    }
    this.form.patchValue({ countries: currentValue });
  }

  submit(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      const key = Object.keys(this.form.controls).find(key => (this.form.controls as any)[key].invalid)!;
      const element = window.document.querySelector(`#${key}`) as HTMLElement;
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const value = this.selected();
    Object.assign(value, this.form.getRawValue());
    const searchParams = sessionStorage.getItem('search-parameter');
    if (searchParams !== null) {
      value.searchParameter = searchParams;
    }
    value.hasCity = this.hasCity();
    value.isRequestMaterial = this.isRequestMaterial();
    this.sending.set(true);
    this.#reservationService.reserve(value)
      .subscribe({
        next: (res: ReserveResponse | null) => {
          if (res === null) {
            return;
          }
          if (!this.hasNextStep()) {
            sessionStorage.removeItem('counseling');
            window.location.href = `${this.completeUrl()}?no=${res.id}`;
          } else {
            sessionStorage.setItem('counseling', JSON.stringify(value));
            this.complete.emit(true);
          }
        },
        error: (error: any) => {
          if (error.status === 409) {
            alert('既にお申込みがあります。\n\nご予約内容（お名前やメールアドレス）に不備があった場合や、カウンセリング日時の確認・変更をご希望の場合は、夢カナ留学の下記電話番号までお問い合わせください。\n\n03-5050-0802');
          }
        }
      })
      .add(() => this.sending.set(false));
  }

  hasError(control: FormControl, errorKey: string) {
    return control.touched && control.hasError(errorKey);
  }

  back(): void {
    this.#reservationService.back();
  }
}
